import {
	Avatar,
	Badge,
	Button,
	ButtonLink,
	Card,
	Link,
	Skeleton,
	Text,
	ToggleButton,
} from "@backstage/ui";
import type {
	BundleInventoryItem,
	BundleInventoryType,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode } from "react";

import { ADMIN_HERO } from "../avd/avdEntitlement";
import { PageFooter, PageHero, TileGrid } from "../layout/pageShell";

import { descriptorFor, type BundleInventoryDescriptor } from "./bundleDescriptors";
import { bundleKey } from "./bundleInventory";
import { primaryAction, type AttentionEntry, type EstateMeasures } from "./estateSummary";
import { ownerLabel, relativeTime } from "./inventoryCells";
import { isTileSelected, type InventoryFilters, type TileId } from "./inventoryFilters";

// The decisions-first top of the admin page, all presentational; the page owns state.
// Behaviour and copy rules: docs/app/avd-registry.md § The admin page.

const useStyles = makeStyles((theme) => ({
	attentionBadge: { color: "var(--bui-fg-warning)" },
	// Unlayered, so it beats BUI's `@layer components` fill for a pressed toggle.
	tile: {
		height: "auto",
		minHeight: 132,
		padding: theme.spacing(2.5),
		textAlign: "left",
		whiteSpace: "normal",
		background: "var(--bui-bg-neutral-1)",
		color: "var(--bui-fg-primary)",
		border: "1px solid var(--bui-border-1)",
		borderRadius: "var(--bui-radius-3)",
		// BUI wraps the children in a row-flex content span; the tile stacks them.
		"& [data-slot=content]": {
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			gap: theme.spacing(0.5),
			width: "100%",
			minHeight: 92,
		},
		"&[data-hovered]": { background: "var(--bui-bg-neutral-1-hover)" },
		"&[data-selected]": {
			background: "var(--bui-bg-neutral-1)",
			color: "var(--bui-fg-primary)",
			border: "2px solid var(--bui-fg-primary)",
			padding: `calc(${theme.spacing(2.5)}px - 1px)`,
		},
	},
	value: { fontSize: "var(--bui-font-size-8)", lineHeight: 1.1, fontWeight: 600 },
	bar: {
		marginTop: "auto",
		width: "100%",
		height: 3,
		borderRadius: 2,
		background: "var(--bui-border-1)",
		overflow: "hidden",
	},
	fill: { display: "block", height: "100%", background: "var(--bui-fg-primary)" },
	list: { display: "grid", gap: theme.spacing(1.25) },
	card: {
		display: "grid",
		gridTemplateColumns: "260px 1fr auto",
		alignItems: "center",
		gap: theme.spacing(3),
		"@media (max-width: 960px)": { gridTemplateColumns: "190px 1fr" },
		"@media (max-width: 640px)": { gridTemplateColumns: "1fr", gap: theme.spacing(2) },
	},
	person: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), minWidth: 0 },
	who: { minWidth: 0, display: "grid", gap: theme.spacing(0.5), justifyItems: "start" },
	name: { overflowWrap: "anywhere" },
	reason: { display: "grid", gap: theme.spacing(0.5) },
	actions: {
		display: "flex",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		"@media (max-width: 960px) and (min-width: 641px)": { gridColumn: 2 },
	},
	skeleton: { display: "grid", gap: theme.spacing(2.5) },
	skeletonRows: { display: "grid", gap: theme.spacing(2), padding: theme.spacing(3) },
}));

/** The eyebrow, the sentence about the estate and the row of attention count, freshness and refresh. */
export function EstateHero(
	props: Readonly<{
		/** Absent while the read has produced nothing: the hero then says only where you are. */
		measures?: EstateMeasures;
		refreshedAt?: Date;
		/** Whether any type was read live, which names the freshness line. */
		anyLive: boolean;
		now: Date;
		loading: boolean;
		onRefresh?: () => void;
	}>,
): ReactNode {
	const classes = useStyles();
	const { measures, refreshedAt, now } = props;
	const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
	return (
		<PageHero
			eyebrow={ADMIN_HERO.eyebrow}
			title={
				measures
					? `${plural(measures.total, "bundle")} across ${plural(measures.colleagues, "colleague")}.`
					: ADMIN_HERO.title
			}
			meta={
				measures ? (
					<>
						{measures.counts.attention > 0 ? (
							<Badge className={classes.attentionBadge}>
								{measures.counts.attention} need attention
							</Badge>
						) : null}
						{refreshedAt ? (
							<Text
								variant="body-small"
								color="secondary"
								title={`${refreshedAt.toLocaleString()} — ${
									props.anyLive
										? "when the server last read a type live. Reading the inventory also reconciles every in-flight row against its GitHub run."
										: "when the browser last read the registry. No type was read live, so this is registry freshness only."
								}`}
							>
								{props.anyLive ? "Checked" : "Reconciled"}{" "}
								{relativeTime(refreshedAt.toISOString(), now)}
							</Text>
						) : null}
						{/* Named for the side effect: the read settles every in-flight row. */}
						<Button
							size="small"
							variant="secondary"
							onPress={props.onRefresh}
							isDisabled={props.loading}
						>
							{props.loading ? "Refreshing…" : "Refresh & settle"}
						</Button>
					</>
				) : undefined
			}
		/>
	);
}

const TILES: { id: TileId; label: string; meaning: string }[] = [
	{ id: "ready", label: "Ready", meaning: "Provisioned and available" },
	{ id: "attention", label: "Need attention", meaning: "Need an operator's decision" },
	{ id: "expiring", label: "Expiring soon", meaning: "Warned; reclaimed unless used or held" },
	{ id: "behind", label: "Behind main", meaning: "Not on this build's IaC" },
];

/** Four filter shortcuts: a count of the whole read, what it means, and its share as a bar. */
export function MeasureTiles(
	props: Readonly<{
		measures: EstateMeasures;
		filters: InventoryFilters;
		onToggle: (tile: TileId) => void;
	}>,
): ReactNode {
	const classes = useStyles();
	const { measures } = props;
	return (
		<TileGrid>
			{TILES.map((tile) => {
				const count = measures.counts[tile.id];
				const share = measures.total ? Math.round((count / measures.total) * 100) : 0;
				return (
					<ToggleButton
						key={tile.id}
						className={classes.tile}
						isSelected={isTileSelected(props.filters, tile.id)}
						onChange={() => props.onToggle(tile.id)}
					>
						<Text variant="body-medium" color="secondary">
							{tile.label}
						</Text>
						<span className={classes.value}>{count}</span>
						<Text variant="body-small" color="secondary">
							{tile.meaning}
						</Text>
						<span className={classes.bar} aria-hidden="true">
							<span className={classes.fill} style={{ width: `${share}%` }} />
						</span>
					</ToggleButton>
				);
			})}
		</TileGrid>
	);
}

/** The bundles that need a decision, with why, and one thing to do about each. */
export function AttentionList(
	props: Readonly<{
		entries: readonly AttentionEntry[];
		types: readonly BundleInventoryType[];
		descriptors: readonly BundleInventoryDescriptor[];
		now: Date;
		onOpen: (item: BundleInventoryItem) => void;
		onHold: (item: BundleInventoryItem, trigger?: HTMLElement) => void;
	}>,
): ReactNode {
	const classes = useStyles();
	return (
		<div className={classes.list}>
			{props.entries.map(({ item, health, worst, more }) => {
				const descriptor = descriptorFor(props.descriptors, item.bundleType);
				const action = primaryAction(item, health, descriptor, props.now);
				const name = item.owner.displayName ?? ownerLabel(item);
				const type =
					props.types.find((t) => t.bundleType === item.bundleType)?.displayName ??
					item.bundleType;
				return (
					<Card key={bundleKey(item)} className={classes.card}>
						<div className={classes.person}>
							<Avatar src="" name={name} purpose="decoration" />
							<div className={classes.who}>
								<Text as="h3" variant="title-x-small" className={classes.name}>
									{name}
								</Text>
								<Badge size="small">{type}</Badge>
							</div>
						</div>
						<div className={classes.reason}>
							<Text as="p" weight="bold">
								{worst.issue}
								{more > 0 ? (
									<Text as="span" variant="body-small" color="secondary">
										{" "}
										+{more} more
									</Text>
								) : null}
							</Text>
							<Text as="p" variant="body-small" color="secondary">
								{worst.why}
							</Text>
						</div>
						<div className={classes.actions}>
							<Button
								variant="secondary"
								size="small"
								onPress={() => props.onOpen(item)}
							>
								Open details
							</Button>
							{action?.kind === "hold" ? (
								<Button
									variant="primary"
									size="small"
									onPress={(e) => props.onHold(item, e.target as HTMLElement)}
								>
									{action.label}
								</Button>
							) : null}
							{action?.kind === "run" ? (
								<ButtonLink
									variant="primary"
									size="small"
									href={action.href}
									target="_blank"
									rel="noopener noreferrer"
								>
									Open run
								</ButtonLink>
							) : null}
						</div>
					</Card>
				);
			})}
		</div>
	);
}

/** The shape of the page while the first read is in flight: hero, four tiles, a table's rows. */
export function PageSkeleton(): ReactNode {
	const classes = useStyles();
	return (
		<div className={classes.skeleton} role="status" aria-label="Loading the bundle registry">
			<div className={classes.skeleton}>
				<Skeleton width={120} height={14} />
				<Skeleton width="min(680px, 90%)" height={44} />
				<Skeleton width={240} height={32} />
			</div>
			<TileGrid>
				{TILES.map((tile) => (
					<Card key={tile.id}>
						<div className={classes.skeleton}>
							<Skeleton width={80} height={14} />
							<Skeleton width={120} height={36} />
						</div>
					</Card>
				))}
			</TileGrid>
			<Skeleton width={240} height={24} />
			<Card>
				<div className={classes.skeletonRows}>
					{[0, 1, 2, 3, 4].map((row) => (
						<Skeleton key={row} width="100%" height={40} />
					))}
				</div>
			</Card>
		</div>
	);
}

/** The line under the page: what this table is not, and where the deeper checks live. */
export function EstateFooter(
	props: Readonly<{ estateReportUrl: string; registrySyncUrl: string }>,
): ReactNode {
	return (
		<PageFooter>
			This page shows what Backstage believes. For cost, orphaned disks and entitlement drift,
			run the <Link href={props.estateReportUrl}>Azure estate report</Link> ·{" "}
			<Link href={props.registrySyncUrl}>AVD registry sync</Link>.
		</PageFooter>
	);
}
